/**
 * VolumeControl — accessibility-first volume widget for the players.
 *
 * Big easy presets (Quiet · Comfortable · Loud) for dementia / hearing-loss
 * users plus a fine slider for caregivers. Always-on at the bottom of the
 * player so nobody has to hunt for it.
 */
import { SpeakerLow, SpeakerHigh, SpeakerNone } from "@phosphor-icons/react";
import { VOLUME_PRESETS } from "../lib/useAudioStabilizer";

const PRESETS = [
    { key: "quiet",       label: "Quiet",       icon: SpeakerLow,  testid: "volume-preset-quiet" },
    { key: "comfortable", label: "Comfortable", icon: SpeakerHigh, testid: "volume-preset-comfortable" },
    { key: "loud",        label: "Loud",        icon: SpeakerHigh, testid: "volume-preset-loud" },
];

export function VolumeControl({ volume, preset, setVolume, setPreset, theme = "dark" }) {
    const isDark = theme === "dark";
    const wrap = isDark
        ? "bg-white/10 backdrop-blur-md border border-white/15 text-white"
        : "bg-[#2F3D6E]/8 backdrop-blur-md border border-[#2F3D6E]/15 text-[#2F3D6E]";
    const presetIdle = isDark
        ? "bg-white/5 hover:bg-white/15 text-white/75"
        : "bg-white/60 hover:bg-white text-[#2F3D6E]/70";
    const presetActive = isDark
        ? "bg-[#A8B5E0] text-[#1a1c25] shadow-lg shadow-[#A8B5E0]/30"
        : "bg-[#4A5A95] text-white shadow-lg shadow-[#4A5A95]/20";

    return (
        <div
            className={`flex items-center gap-2 sm:gap-3 px-3 py-2 rounded-full ${wrap}`}
            data-testid="volume-control"
        >
            {PRESETS.map((p) => {
                const Icon = p.icon;
                const active = preset === p.key;
                return (
                    <button
                        key={p.key}
                        onClick={() => setPreset(p.key)}
                        className={`group inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 ${active ? presetActive : presetIdle}`}
                        data-testid={p.testid}
                        aria-label={`Set volume to ${p.label} (${Math.round(VOLUME_PRESETS[p.key] * 100)}%)`}
                        aria-pressed={active}
                    >
                        <Icon size={14} weight={active ? "fill" : "regular"} />
                        <span className="hidden sm:inline">{p.label}</span>
                    </button>
                );
            })}

            {/* Fine slider — caregivers who want to dial it in */}
            <div className="hidden md:flex items-center gap-2 pl-2 ml-1 border-l border-white/10">
                {volume === 0 ? (
                    <SpeakerNone size={14} className={isDark ? "text-white/60" : "text-[#2F3D6E]/60"} />
                ) : (
                    <SpeakerLow size={14} className={isDark ? "text-white/60" : "text-[#2F3D6E]/60"} />
                )}
                <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    aria-label="Audio volume"
                    className={`w-20 accent-[#A8B5E0] cursor-pointer ${isDark ? "" : "accent-[#4A5A95]"}`}
                    data-testid="volume-slider"
                />
                <span className={`text-[10px] tabular-nums w-6 text-right ${isDark ? "text-white/55" : "text-[#2F3D6E]/55"}`} data-testid="volume-percent">
                    {Math.round(volume * 100)}
                </span>
            </div>
        </div>
    );
}
